import axios from "axios";
import { QueryClient } from "react-query";

/**
 * CSRF Token Utility
 *
 * Provides centralized CSRF token management for Django backend integration.
 * Handles token extraction from cookies and proactive fetching.
 */

/**
 * Extract CSRF token from browser cookies
 * @returns CSRF token string or null if not found
 */
export const getCsrfTokenFromCookie = (): string | null => {
  const cookieString = document.cookie;

  if (!cookieString) {
    return null;
  }

  const cookies = cookieString
    .split("; ")
    .reduce((acc: Record<string, string>, current) => {
      const firstEqualIndex = current.indexOf("=");
      if (firstEqualIndex > 0) {
        const key = current.slice(0, firstEqualIndex);
        const value = current.slice(firstEqualIndex + 1);
        try {
          acc[key] = decodeURIComponent(value);
        } catch {
          // Fallback to raw value if decoding fails
          acc[key] = value;
        }
      }
      return acc;
    }, {} as Record<string, string>);

  return cookies.csrftoken || null;
};

/**
 * Fetch a fresh CSRF token from the backend
 * Makes a GET request to /auth/csrf/ which triggers Django to set the csrftoken cookie
 *
 * @returns Promise resolving to CSRF token string or null if fetch fails
 */
export const fetchCsrfToken = async (): Promise<string | null> => {
  try {
    // Make GET request to CSRF endpoint to trigger cookie setting
    const response = await axios.get("/auth/csrf/", {
      withCredentials: true, // Required to receive and store cookies
    });

    // Try to extract token from response header first
    const headerToken = response.headers["x-csrftoken"];
    if (headerToken) {
      return headerToken;
    }

    // Fallback: extract from cookie after backend sets it
    const cookieToken = getCsrfTokenFromCookie();

    if (!cookieToken) {
      console.warn(
        "CSRF token fetch succeeded but token not found in cookie or header. " +
          "This may indicate a backend configuration issue."
      );
    }

    return cookieToken;
  } catch (error) {
    // Graceful degradation - log warning but don't block user flow
    console.warn(
      "Failed to fetch CSRF token from /auth/csrf/. " +
        "Login may fail if backend requires CSRF protection.",
      error
    );
    return null;
  }
};

/**
 * React Query integration for fetching CSRF token with built-in deduplication
 * Uses React Query's cache to prevent concurrent requests
 *
 * @param queryClient - React Query client instance for manual fetching
 * @returns Promise resolving to CSRF token string or null
 */
export const fetchCsrfTokenWithQuery = async (
  queryClient: QueryClient
): Promise<string | null> => {
  const token = await queryClient.fetchQuery({
    queryKey: ["csrfToken"],
    queryFn: fetchCsrfToken,
    staleTime: 5 * 60 * 1000, // Consider token fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  return token;
};
